import { useState, useRef, useEffect } from 'react'
import { ChevronDown, Bot, Calendar } from 'lucide-react'
import { useTranslation } from 'react-i18next'

interface Assistant {
  id: string
  name: string
  is_active: boolean
  google_email?: string | null
}

interface AssistantSelectorProps {
  assistants: Assistant[]
  selectedId: string | null
  onChange: (assistantId: string | null) => void
  loading?: boolean
}

export function AssistantSelector({
  assistants,
  selectedId,
  onChange,
  loading
}: AssistantSelectorProps) {
  const { t } = useTranslation('agenda')
  const [isOpen, setIsOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setIsOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const selectedAssistant = assistants.find(a => a.id === selectedId)

  if (assistants.length === 0) {
    return null
  }

  if (assistants.length === 1) {
    return (
      <div className="flex items-center gap-2 px-3 py-2 rounded-xl bg-[#DBEAFE] border border-[#BFDBFE] dark:bg-blue-500/20 dark:border-blue-500/30 dark:rounded-lg">
        <div className="w-6 h-6 rounded-full bg-[#2563EB]/15 flex items-center justify-center dark:bg-blue-500/20">
          <Bot className="w-3.5 h-3.5 text-[#2563EB] dark:text-[#60A5FA]" />
        </div>
        <span className="text-sm text-[#2563EB] font-medium max-w-[150px] truncate dark:text-[#60A5FA]">
          {assistants[0].name}
        </span>
      </div>
    )
  }

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => !loading && setIsOpen(!isOpen)}
        disabled={loading}
        className="flex items-center gap-2 px-3 py-2 rounded-xl bg-[#DBEAFE] border border-[#BFDBFE] hover:bg-[#BFDBFE]/50 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed dark:bg-[#27272A] dark:border-[#3F3F46] dark:hover:border-neutral-500 dark:rounded-lg"
      >
        {selectedAssistant ? (
          <>
            <div className="w-6 h-6 rounded-full bg-[#2563EB]/15 flex items-center justify-center dark:bg-blue-500/20">
              <Bot className="w-3.5 h-3.5 text-[#2563EB] dark:text-[#60A5FA]" />
            </div>
            <span className="text-sm text-[#2563EB] font-medium max-w-[150px] truncate dark:text-[#60A5FA]">
              {selectedAssistant.name}
            </span>
          </>
        ) : (
          <>
            <div className="w-6 h-6 rounded-full bg-[#EEF4FF] flex items-center justify-center dark:bg-neutral-700">
              <Calendar className="w-3.5 h-3.5 text-[#2563EB] dark:text-neutral-400" />
            </div>
            <span className="text-sm text-[#64748B] dark:text-[#E4E4E7]">{t('pickAssistant')}</span>
          </>
        )}
        <ChevronDown className={`w-4 h-4 text-[#2563EB] dark:text-[#A1A1AA] transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {isOpen && (
        <div className="absolute z-50 mt-1 w-64 max-h-80 overflow-auto rounded-xl bg-white border border-border shadow-md dark:bg-[#27272A] dark:border-[#3F3F46] dark:rounded-lg dark:shadow-xl">
          <div className="px-3 py-2 border-b border-border dark:border-neutral-700">
            <p className="text-xs text-[#64748B] uppercase tracking-wider dark:text-[#A1A1AA]">
              {t('connectedAssistants')}
            </p>
          </div>

          {assistants.map(assistant => (
            <button
              key={assistant.id}
              type="button"
              onClick={() => {
                onChange(assistant.id)
                setIsOpen(false)
              }}
              className={`w-full flex items-center gap-3 px-3 py-2.5 text-left hover:bg-[#F1F5F9] transition-colors duration-200 dark:hover:bg-neutral-700/50 ${
                selectedId === assistant.id ? 'bg-[#EEF4FF] dark:bg-blue-500/10' : ''
              }`}
            >
              <div className="w-8 h-8 rounded-full bg-[#DBEAFE] flex items-center justify-center flex-shrink-0 dark:bg-blue-500/20">
                <Bot className="w-4 h-4 text-[#2563EB] dark:text-[#60A5FA]" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm text-[#0F172A] truncate dark:text-[#FAFAFA]">{assistant.name}</p>
                {assistant.google_email && (
                  <p className="text-xs text-[#64748B] truncate dark:text-[#D4D4D8]">{assistant.google_email}</p>
                )}
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  )
}
